// src/pages/winback/WinbackDashboardPage.jsx
import React, { useState, useEffect } from 'react';
import { get } from '../../helpers/apiHelper';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserClock } from '@fortawesome/free-solid-svg-icons';
import WinbackKPIs from '../../components/winback/WinbackKPIs';
import InactiveCustomersTable from '../../components/winback/InactiveCustomersTable';
import WinbackStatsCharts from '../../components/winback/WinbackStatsCharts';
import WinbackConfigForm from '../../components/winback/WinbackConfigForm';
import CampaignHistoryTable from '../../components/winback/CampaignHistoryTable';
import WinbackActivationChecklist from '../../components/winback/WinbackActivationChecklist';

// Estilos personalizados para react-select con z-index alto
const selectStyles = {
    menu: (provided) => ({
        ...provided,
        zIndex: 9999
    }),
    menuPortal: (provided) => ({
        ...provided,
        zIndex: 9999
    })
};

const WinbackDashboardPage = () => {
    const [marcas, setMarcas] = useState([]);
    const [selectedMarca, setSelectedMarca] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);

    useEffect(() => {
        const fetchMarcas = async () => {
            try {
                const response = await get('marcas');
                setMarcas(response);
                if (response.length > 0) {
                    setSelectedMarca(response[0]);
                }
            } catch (error) {
                console.error('Error fetching marcas:', error);
                Swal.fire('Error', 'No se pudieron cargar las marcas', 'error');
            } finally {
                setLoading(false);
            }
        };

        fetchMarcas();
    }, []);

    const marcaOptions = marcas.map(m => ({
        value: m._id,
        label: m.nombre,
        ...m
    }));

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="spinner-border animate-spin inline-block w-12 h-12 border-4 rounded-full text-purple-600" role="status">
                        <span className="sr-only">Cargando...</span>
                    </div>
                    <p className="mt-4 text-gray-600">Cargando WinBack Dashboard...</p>
                </div>
            </div>
        );
    }

    if (!selectedMarca) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <FontAwesomeIcon icon={faUserClock} className="text-6xl text-gray-400 mb-4" />
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">No hay marcas disponibles</h2>
                    <p className="text-gray-600">Crea una marca primero para usar WinBack</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50">
            {/* Header Hero */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <div className="text-center">
                        <div className="flex items-center justify-center mb-4">
                            <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-4">
                                <FontAwesomeIcon icon={faUserClock} className="text-3xl" />
                            </div>
                            <div>
                                <h1 className="text-4xl font-bold mb-2">Recupera Clientes Perdidos</h1>
                                <p className="text-purple-100 text-lg">
                                    Reduce tu temporada baja recuperando clientes inactivos automáticamente
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

                {/* Selector de Marca */}
                <div className="mb-8">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Selecciona una marca
                    </label>
                    <Select
                        options={marcaOptions}
                        value={{ value: selectedMarca._id, label: selectedMarca.nombre }}
                        onChange={(option) => setSelectedMarca(option)}
                        className="max-w-md"
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                    />
                </div>

                {/* Checklist de Activación */}
                <WinbackActivationChecklist key={`checklist-${refreshKey}`} marcaId={selectedMarca._id} />

                {/* KPIs */}
                <WinbackKPIs key={`kpis-${refreshKey}`} marcaId={selectedMarca._id} />

                {/* Grid de Estadísticas y Configuración */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                    {/* Gráficos */}
                    <div className="bg-white rounded-xl shadow-lg p-6">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">Estadísticas</h2>
                        <WinbackStatsCharts key={`charts-${refreshKey}`} marcaId={selectedMarca._id} />
                    </div>

                    {/* Configuración */}
                    <div id="winback-config-section" className="bg-white rounded-xl shadow-lg p-6">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">Configuración</h2>
                        <WinbackConfigForm
                            marcaId={selectedMarca._id}
                            onConfigSaved={() => {
                                // Forzar refresh de todos los componentes relacionados
                                setRefreshKey(prev => prev + 1);
                            }}
                        />
                    </div>
                </div>

                {/* Clientes Inactivos */}
                <div id="inactive-customers-section" className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Clientes Inactivos</h2>
                    <InactiveCustomersTable key={`inactive-${refreshKey}`} marcaId={selectedMarca._id} />
                </div>

                {/* Historial de Campañas */}
                <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Historial de Campañas</h2>
                    <CampaignHistoryTable key={`campaigns-${refreshKey}`} marcaId={selectedMarca._id} />
                </div>

            </div>
        </div>
    );
};

export default WinbackDashboardPage;

